import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import gql from 'graphql-tag';
import { GetPayoutReportsQuery, GetPayoutReportsQueryVariables } from '../gql-types.generated';
import { getStandardHeaders } from '../util/StandardRequestHeaders';

/**
 * Payout report history for the Reports page.
 *
 * Deliberately NOT part of getTenantAccount, where these fields used to live, for three
 * reasons. The year is chosen by a chip on this page, and getTenantAccount runs once at
 * startup for the whole app, so there is nowhere to put the argument. Every user paid for
 * this read on every page even though the Reports route is gated behind a capability some
 * tenants do not have. And getTenantAccount is re-run on a 2 second poll after account
 * mutations, which would repeat the read several times for a page that is not open.
 *
 * payoutReportYears is selected alongside the reports rather than in a request of its own:
 * it is what the year chips are built from, and the two are always needed together.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
const QUERY_PAYOUT_REPORTS = gql`
  query getPayoutReports($year: Int) {
    account {
      id
      payoutReportYears
      payoutReports(year: $year) {
        payoutReportId
        startTime
        endTime
        payoutCreateTime
        payoutCompleteTime
      }
    }
  }
`;

export interface PayoutReportsResult {
  years: number[];
  reports: NonNullable<NonNullable<GetPayoutReportsQuery['account']>['payoutReports']>;
}

/**
 * Omitting year asks the server for the most recent year that has reports, which is the
 * same year the page would select on load. That keeps the first request to one round trip
 * rather than fetching the year list and then fetching the year it names.
 */
export const queryPayoutReports = async (client: ApolloClient<NormalizedCacheObject>, year?: number): Promise<PayoutReportsResult> => {
  const headers = await getStandardHeaders();
  const { data, errors } = await client.query<GetPayoutReportsQuery, GetPayoutReportsQueryVariables>({
    query: QUERY_PAYOUT_REPORTS,
    variables: { year },
    context: { headers },
    // Apollo keys a field by its arguments, so each year caches separately and revisiting
    // a year would otherwise never re-read it. The year LIST is the reason that matters:
    // a payout completing while the page is open opens a new year, and a cached read
    // would leave the merchant with no chip for it until a full reload.
    fetchPolicy: 'network-only',
  });

  if (errors && errors.length > 0) {
    errors.forEach(error => {
      // Log error details in the console.
      console.error(error);
    });
    // Friendly error to the user.
    throw new Error('An error has occurred during the query.');
  }

  return {
    years: data?.account?.payoutReportYears ?? [],
    reports: data?.account?.payoutReports ?? [],
  };
};
